import { ScrollArea } from '@neo/primitives/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { downloadFile } from '@shared/utils'
import { memo } from 'react'
import { ErrorBoundary } from 'v2/shared/components/error-boundary'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { getFormattedTime } from '../../run-info/analysis-v2/utils'
import { PanelWrapper } from '../../run-info/components/panel-wrapper'
import { useFlowStore } from '../../store'
import type { EvalStreamEntry } from '../../types'
import { EVENT_LABELS } from './constants'
import { EvalActions } from './eval-actions'
import { EvalControlButtons } from './eval-control-buttons'
import { EvalInterface } from './eval-interface'
import { ResultsTree } from './eval-interface/results-tree'
import { useEvalEvents } from './hooks/use-eval-events'
import { useEvalNodeHighlight } from './hooks/use-eval-node-highlight'
import { useEvalStore } from './store'

const downloadEvalDataAsJson = (events: EvalStreamEntry[]) => {
  const formattedData = events.map((entry) => ({
    type: entry.type,
    label: EVENT_LABELS[entry.type] ?? entry.type,
    timestamp: getFormattedTime(entry.receivedAt.toString()),
    payload: entry.payload,
  }))
  const dataString = JSON.stringify(formattedData, null, 2)
  const blob = new Blob([dataString], { type: 'application/json' })
  const filename = `simulation-data-${new Date().toISOString().split('T')[0]}.json`
  downloadFile('', filename, blob)
}

const ResultsTab = () => {
  return (
    <div className="relative flex-1 min-h-0">
      <ScrollArea className="h-full w-full" scrollHideDelay={10}>
        <div className="p-4 pb-8">
          <ResultsTree />
        </div>
      </ScrollArea>
    </div>
  )
}

const EvalSidebarContent = () => {
  const events = useEvalStore((state) => state.events)
  const clearEvalStream = useEvalStore((state) => state.clearEvalStream)
  const { isDebuggingMode } = useFeatureFlags()

  return (
    <>
      {isDebuggingMode ? (
        <Tabs defaultValue="results" className="flex flex-col h-full">
          <div className="sticky top-0 z-10 flex items-center justify-between px-2 pb-2 bg-background">
            <TabsList variant="default" className="h-8 w-fit">
              <TabsTrigger
                value="raw"
                variant="default"
                className="text-xs-400 px-3 py-1"
                data-testid="tab-raw-data"
              >
                Raw Data
              </TabsTrigger>
              <TabsTrigger
                value="results"
                variant="default"
                className="text-xs-400 px-3 py-1"
                data-testid="tab-results"
              >
                Results
              </TabsTrigger>
            </TabsList>
            <EvalActions
              events={events}
              onDownload={() => downloadEvalDataAsJson(events)}
              onClear={clearEvalStream}
            />
          </div>
          <TabsContent value="raw" className="flex-1 m-0 min-h-0">
            <EvalInterface />
          </TabsContent>
          <TabsContent value="results" className="flex-1 m-0 min-h-0">
            <ResultsTab />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="flex flex-col h-full">
          <ResultsTab />
        </div>
      )}
      <EvalControlButtons />
    </>
  )
}

export const EvalSidebar = memo(() => {
  useEvalEvents()
  useEvalNodeHighlight()
  const showEvalSidebar = useFlowStore((state) => state.showEvalSidebar)

  if (showEvalSidebar) {
    return (
      <ErrorBoundary>
        <div data-testid="eval-sidebar" className="flex justify-between bottom-3 h-0 relative">
          <PanelWrapper
            title="Running simulation"
            headerBgColor="bg-background"
            headerTextColor="text-primary"
            className="ml-4 absolute bottom-0 left-0"
            width={{ minimized: 280, normal: 580 }}
            onClose={() => useFlowStore.getState().setShowEvalSidebar(false)}
          >
            <EvalSidebarContent />
          </PanelWrapper>
        </div>
      </ErrorBoundary>
    )
  }
  return null
})
